import { useEffect, useState } from "react";
import { SmartChat } from "@/components/chat/SmartChat";
import type { ChatScope } from "@/lib/api/agent";
import type { ChatContextPack } from "@/lib/chat-context-packs";
import { cn } from "@/lib/utils";

interface Props {
  open: boolean;
  onClose: () => void;
  scope: ChatScope;
  buildingId?: string;
  contextPack?: ChatContextPack;
  panelClassName?: string;
}

// Backdrop + slide-in panel shared by every chat entry point (perspective hero CTA and the
// on-demand Konfigurointiassistentti). Mounting SmartChat is deferred until first open — and
// stays mounted afterward, so re-closing doesn't drop the conversation — so a session, and any
// mode-driven kickoff, starts only once the user actually opens the drawer.
export function ChatDrawerPanel({
  open,
  onClose,
  scope,
  buildingId,
  contextPack,
  panelClassName,
}: Props) {
  const [ever_opened, set_ever_opened] = useState(false);
  useEffect(() => {
    if (open) set_ever_opened(true);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  return (
    <>
      <div
        onClick={onClose}
        className={`fixed inset-0 z-40 bg-black/30 backdrop-blur-sm transition-opacity duration-300
          ${open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"}`}
      />
      <div
        className={cn(
          "fixed top-0 right-0 z-50 h-screen w-full shadow-2xl transition-transform duration-300 ease-in-out",
          panelClassName,
          open ? "translate-x-0" : "translate-x-full",
        )}
      >
        {ever_opened && (
          <SmartChat
            key={JSON.stringify(scope)}
            scope={scope}
            buildingId={buildingId}
            className="h-full rounded-none border-0"
            onClose={onClose}
            contextPack={contextPack}
            active={open}
          />
        )}
      </div>
    </>
  );
}
